import {
  LegacyCard,
  Page,
  Layout,
  Link,
  TextContainer,
  Text,
  Modal,
  TextField,
  Button,
  HorizontalStack,
  VerticalStack,
  Spinner,
  Box,
  Avatar,
} from '@shopify/polaris';
import { DeleteMinor } from '@shopify/polaris-icons';
import { useState, useEffect, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { deleteLead, getLead, getLeadNotes, postLeadNote, deleteLeadNote } from '../../../services/leadsService';
import { EditLeadModal } from '../../UI/Leads/EditLeadModal';
import { checkPermission } from '../../../services/permissionsService';
import { genderLabel } from '../../../helpers/leadGender';
import ToastContext from '../../../context/ToastContext';
import { SET_OK, SET_ERROR } from '../../../context/ToastReducer';

const formatDate = (value) =>
  value &&
  Intl.DateTimeFormat('es-mx', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(value));

// Fecha + hora legible para el timeline de notas. Ej: "12 jun 2026, 14:30".
const formatDateTime = (value) =>
  value &&
  Intl.DateTimeFormat('es-mx', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

// Iniciales del autor para el avatar del timeline (máx 2 letras).
const authorInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ViewLead = () => {
  const { id } = useParams();
  const history = useHistory();
  const [, dispatchToast] = useContext(ToastContext);
  const queryClient = useQueryClient();
  const [lead, setLead] = useState(false);
  const [active, setActive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [noteBody, setNoteBody] = useState('');

  const returnMatch = window.location.search.match(/\??r=(.+)&?/);
  const returnUrl = returnMatch && returnMatch.length > 1 ? window.atob(returnMatch[1]) : '/leads';

  useEffect(() => {
    getLead(id).then(({ data }) => setLead(data));
    // eslint-disable-next-line
  }, []);

  // --- Notas (timeline) ---------------------------------------------------
  const notesKey = ['leadNotes', id];
  const { data: notes = [], isLoading: notesLoading } = useQuery(notesKey, () =>
    getLeadNotes(id).then((r) => r.data.notes || []),
  );

  const addNote = useMutation((body) => postLeadNote(id, body), {
    onSuccess: (r) => {
      dispatchToast({ type: SET_OK, payload: r.data });
      setNoteBody('');
      queryClient.invalidateQueries(notesKey);
    },
    onError: (e) => {
      dispatchToast({ type: SET_ERROR, payload: e.response?.data || { message: 'No se pudo añadir la nota' } });
    },
  });

  const removeNote = useMutation((noteId) => deleteLeadNote(id, noteId), {
    onSuccess: (r) => {
      dispatchToast({ type: SET_OK, payload: r.data });
      queryClient.invalidateQueries(notesKey);
    },
    onError: (e) => {
      dispatchToast({ type: SET_ERROR, payload: e.response?.data || { message: 'No se pudo borrar la nota' } });
    },
  });

  const handleAddNote = () => {
    const body = noteBody.trim();
    if (!body) {
      dispatchToast({ type: SET_ERROR, payload: { message: 'La nota no puede estar vacía' } });
      return;
    }
    addNote.mutate(body);
  };

  const handleDelete = () => {
    deleteLead(id)
      .then((r) => {
        dispatchToast({ type: SET_OK, payload: r.data });
        history.push(returnUrl);
      })
      .catch((e) => {
        dispatchToast({ type: SET_ERROR, payload: e.response.data });
        setConfirmDelete(false);
      });
  };

  if (!lead) return null;

  const fullName = [lead.name, lead.surname].filter(Boolean).join(' ');
  const categories = lead.interestCategory ? lead.interestCategory.split(':').filter(Boolean) : [];

  return (
    <Page
      title={fullName || 'Contacto'}
      subtitle={`Contacto desde ${formatDate(lead.createdAt) || '—'}`}
      primaryAction={{ content: 'Editar', onAction: () => setActive(true), disabled: !checkPermission('LEAD:UPDATE') }}
      secondaryActions={
        checkPermission('LEAD:DELETE')
          ? [{ content: 'Eliminar', destructive: true, onAction: () => setConfirmDelete(true) }]
          : undefined
      }
      backAction={{ content: 'Ver contactos', url: returnUrl }}
      divider
    >
      <Layout>
        <Layout.Section>
          <LegacyCard title="Contacto">
            <LegacyCard.Section>
              <TextContainer>
                {lead.email ? (
                  <Text as="p">
                    <Link external url={`mailto:${lead.email}`}>
                      {lead.email}
                    </Link>
                  </Text>
                ) : (
                  <Text as="p" color="subdued">
                    Sin email
                  </Text>
                )}
                <Text as="p">Tel: {lead.phone || '—'}</Text>
              </TextContainer>
            </LegacyCard.Section>
          </LegacyCard>

          <LegacyCard title="Interés">
            <LegacyCard.Section>
              <Text as="p">Género: {genderLabel(lead.interestGender)}</Text>
              <Text as="p">Categorías: {categories.length ? categories.join(', ') : '—'}</Text>
            </LegacyCard.Section>
          </LegacyCard>

          {lead.notes && (
            <LegacyCard title="Observaciones">
              <LegacyCard.Section>
                <Text as="p">{lead.notes}</Text>
              </LegacyCard.Section>
            </LegacyCard>
          )}

          <LegacyCard title="Comentarios">
            {checkPermission('LEAD:UPDATE') && (
              <LegacyCard.Section>
                <VerticalStack gap="2">
                  <TextField
                    label="Nuevo comentario"
                    labelHidden
                    value={noteBody}
                    onChange={setNoteBody}
                    multiline={2}
                    autoComplete="off"
                    placeholder="Deja un comentario…"
                  />
                  <HorizontalStack align="end">
                    <Button primary onClick={handleAddNote} loading={addNote.isLoading} disabled={!noteBody.trim()}>
                      Añadir comentario
                    </Button>
                  </HorizontalStack>
                </VerticalStack>
              </LegacyCard.Section>
            )}

            <LegacyCard.Section>
              {notesLoading ? (
                <Box paddingBlockStart="2" paddingBlockEnd="2">
                  <Spinner accessibilityLabel="Cargando notas" size="small" />
                </Box>
              ) : notes.length === 0 ? (
                <Text as="p" color="subdued">
                  Todavía no hay actividad.
                </Text>
              ) : (
                <div>
                  {notes.map((note, idx) => {
                    const isLast = idx === notes.length - 1;
                    return (
                      <div key={note.id} style={{ display: 'flex', gap: '12px' }}>
                        {/* Columna del timeline: punto/avatar + línea vertical */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Avatar
                            size="small"
                            name={note.author?.name || 'Sistema'}
                            initials={authorInitials(note.author?.name)}
                          />
                          {!isLast && (
                            <div
                              style={{
                                width: '2px',
                                flex: 1,
                                minHeight: '12px',
                                marginTop: '4px',
                                background: 'var(--p-color-border-subdued, #e1e3e5)',
                              }}
                            />
                          )}
                        </div>
                        {/* Contenido de la nota */}
                        <div style={{ flex: 1, paddingBottom: isLast ? 0 : '16px' }}>
                          <HorizontalStack align="space-between" blockAlign="start" wrap={false}>
                            <Box>
                              <Text as="span" fontWeight="semibold">
                                {note.author?.name || 'Sistema'}
                              </Text>
                              <Text as="p" variant="bodySm" color="subdued">
                                {formatDateTime(note.createdAt) || '—'}
                              </Text>
                            </Box>
                            {checkPermission('LEAD:UPDATE') && (
                              <Button
                                icon={DeleteMinor}
                                plain
                                accessibilityLabel="Borrar comentario"
                                onClick={() => removeNote.mutate(note.id)}
                                loading={removeNote.isLoading && removeNote.variables === note.id}
                              />
                            )}
                          </HorizontalStack>
                          <Box paddingBlockStart="1">
                            <Text as="p">{note.body}</Text>
                          </Box>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>

        <Layout.Section secondary>
          <LegacyCard title="Origen">
            <LegacyCard.Section>
              <Text as="p">Fuente: {lead.source || '—'}</Text>
            </LegacyCard.Section>
            <LegacyCard.Section title="Consentimiento">
              <Text as="p">{lead.consent ? 'Otorgado' : 'No otorgado'}</Text>
              {lead.consentAt && (
                <Text as="p" color="subdued">
                  El {formatDate(lead.consentAt)}
                </Text>
              )}
              {lead.consentVersion && (
                <Text as="p" color="subdued">
                  Versión {lead.consentVersion}
                </Text>
              )}
            </LegacyCard.Section>
          </LegacyCard>

          <LegacyCard title="Centros">
            <LegacyCard.Section>
              {lead.deposits && lead.deposits.length > 0 ? (
                lead.deposits.map((d) => (
                  <Text as="p" key={d.id}>
                    {d.deposit ? `${d.deposit.name} (${d.deposit.code})` : `#${d.depositId}`}
                  </Text>
                ))
              ) : (
                <Text as="p" color="subdued">
                  Sin centros asociados
                </Text>
              )}
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
      </Layout>

      {active && (
        <EditLeadModal
          active={active}
          lead={lead}
          id={id}
          onSave={(c) => setLead({ ...lead, ...c })}
          onClose={() => setActive(false)}
        />
      )}

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Eliminar contacto"
        primaryAction={{ content: 'Eliminar', destructive: true, onAction: handleDelete }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setConfirmDelete(false) }]}
      >
        <Modal.Section>
          <Text as="p">
            ¿Seguro que quieres eliminar el contacto <strong>{fullName}</strong>? Esta acción no se puede deshacer.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default ViewLead;
